import { ChangeDetectionStrategy, Component, forwardRef, inject, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { IProductDataV3 } from '@df/catalog/product/product';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsContent } from '@icms/core/content';
import each from 'lodash-es/each';
import { NPitchPrint } from 'projects/app-default/src/app/module/pitch-print/pitch-print.namespace';
import { PitchPrintService } from 'projects/app-default/src/app/module/pitch-print/pitch-print.service';
import { InlineButtonComponent } from '../../../../common/buttons/inline-button/inline-button.component';
import { Product } from '../../product.decorator';
import { ProductViewAddToBasketComponent } from '../add-to-basket/product-view-add-to-basket.component';
import { ProductViewConfigurableOptionsComponent } from '../configurable-options/product-view-configurable-options.component';
import { NProductViewLayout } from '../layout/product-view-layout.namespace';
import { IProjectSavedEvent, ProductViewPitchPrintButtonComponent } from '../pitch-print-button/product-view-pitch-print-button.component';
import { ProductViewPriceComponent } from '../price/product-view-price.component';
import { ProductViewComponent } from '../product-view.component';
import { ProducViewQtySelectComponent } from '../qty-select/product-view-qty-select.component';
import { ProductViewDetailsBottomListComponent } from './bottom-list/product-view-details-bottom-list.component';
import { ProductViewDetailsNameComponent } from './name/product-view-details-name.component';
import { ProductViewDetailsQuoteComponent } from './quote/product-view-details-quote.component';

@Component({
    imports: [
        ProductViewPriceComponent,
        forwardRef(() => ProductViewConfigurableOptionsComponent),
        ProductViewAddToBasketComponent,
        ProductViewDetailsNameComponent,
        ProductViewDetailsBottomListComponent,
        ProductViewDetailsQuoteComponent,
        ProducViewQtySelectComponent,
        TranslatePipe,
        GridModule,
        ProductViewPitchPrintButtonComponent,
        InlineButtonComponent
    ],
    selector: 'product-view-details',
    templateUrl: 'product-view-details.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductViewDetailsComponent extends UiComponent implements OnChanges, OnDestroy {
    @Input()
    product?: Product<IProductDataV3>;

    @Input()
    @ICmsContent({
        watchDepth: 1
    })
    content?: NProductViewLayout.IBlockDataContent;

    @ViewChild(ProductViewPitchPrintButtonComponent)
    productViewPitchPrintButtonComponent?: ProductViewPitchPrintButtonComponent;

    private pitchPrintProjects = new Map<number, NPitchPrint.IProject>();

    readonly productView = inject(ProductViewComponent);

    protected productConfiguration = inject(ProductConfiguration);

    private pitchPrintService = inject(PitchPrintService);

    override ngOnInit(): void {
        super.ngOnInit();
        const pitchPrintProjects = this.pitchPrintService.getProjectsFromStorage();
        if (pitchPrintProjects) {
            each(pitchPrintProjects, (project, productId) => {
                const numberId = parseInt(productId);
                if (numberId && numberId.toString() === productId && project?.projectId) {
                    this.pitchPrintProjects.set(numberId, project);
                }
            });
        }
        this.subscriptions.add(
            this.productConfiguration.submitBefore$.subscribe(data => {
                if (data && this.isArtPanel && this.currentProject) {
                    try {
                        const _pitchprint = encodeURIComponent(JSON.stringify(this.currentProject));
                        if (_pitchprint) {
                            data.forEach(item => {
                                if (item.id === this.product?.id) {
                                    item['_pitchprint'] = _pitchprint;
                                }
                            });
                        }
                    } catch (e) {}
                }
            })
        );
    }

    override ngOnChanges(changes: any): void {
        super.ngOnChanges(changes);
        if (changes.product && this.product?.isQuote) {
            this.qty = 1;
            this.detectChanges();
        }
    }

    protected get isSM(): boolean {
        return this.client.sizeId < 2;
    }

    protected get isComplex(): boolean {
        return !!this.product?.isComplexPanel;
    }

    protected get isQuote(): boolean {
        return !!this.product?.isQuote;
    }

    get qty(): number {
        return this.productConfiguration.qty$.value;
    }

    set qty(value: number) {
        this.productConfiguration.qty$.next(value);
    }

    protected get variationDescription(): string | undefined {
        return this.product?.getAttr('variation_description');
    }

    protected get leadTime(): string | undefined {
        return this.product?.getAttr('lead_time');
    }

    protected get buttonsGridSize(): number {
        if (this.isArtPanel && this.client.sizeId > 1) {
            return 2;
        }
        return 1;
    }

    protected get isArtPanel(): boolean {
        return !!this.product?.isArtPanel;
    }

    private getPitchPrintProject(productId: number): NPitchPrint.IProject | undefined {
        return this.pitchPrintProjects.get(productId);
    }

    protected get currentProject(): NPitchPrint.IProject | undefined {
        if (this.product && this.isArtPanel) {
            return this.getPitchPrintProject(this.product.id);
        }
        return undefined;
    }

    protected onProjectSaved(event: IProjectSavedEvent): void {
        const { productId, project } = event;
        if (productId && project) {
            this.pitchPrintProjects.set(productId, project);
        }
        this.detectChanges();
    }

    protected onAddedToBasket(): void {
        if (this.isArtPanel && this.product) {
            const productId = this.product.id;
            this.pitchPrintProjects.delete(productId);
            this.pitchPrintService.removeProjectFromStorage(productId);
            this.productViewPitchPrintButtonComponent?.initializeForNewProject();
            this.detectChanges();
        }
    }

    protected get addToBasketDisabled(): boolean {
        if (this.isArtPanel) {
            return !this.currentProject;
        }
        return false;
    }

    protected downloadPdf(): void {
        if (this.productViewPitchPrintButtonComponent) {
            this.productViewPitchPrintButtonComponent.downloadPdf();
        }
    }
}
